import { assign, fromPromise, setup } from 'xstate';
import {
  emptyCdsForm,
  emptyDeaForm,
  LICENSES_TABS,
  openDeleteConfirmation,
  populateCdsForm,
  populateDeaForm,
  setCdsFromOutput,
  setCdsTableFilter,
  setCdsTableSort,
  setDeaFromOutput,
  setDeaTableFilter,
  setDeaTableSort,
} from './utils/actions';
import { CDS_API_CALLS, DEA_API_CALLS } from './utils/api';
import {
  Context,
  Events,
  OnEditEvent,
  OnSetActiveTabEvent,
  OnSetProviderId,
  Outputs,
  Scope,
} from './utils/types';

import { showAxiosErrorAction } from '~/features/actions';

export const licensesFormMachine = setup({
  actors: {
    // Cds Api Handlers
    getCdsLicenses: fromPromise(CDS_API_CALLS.get),
    updateCdsLicense: fromPromise(CDS_API_CALLS.update),
    createCdsLicense: fromPromise(CDS_API_CALLS.create),
    deleteCdsLicense: fromPromise(CDS_API_CALLS.delete),
    // Dea Api Handlers
    getDeaLicenses: fromPromise(DEA_API_CALLS.get),
    updateDeaLicense: fromPromise(DEA_API_CALLS.update),
    createDeaLicense: fromPromise(DEA_API_CALLS.create),
    deleteDeaLicense: fromPromise(DEA_API_CALLS.delete),
  },
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
    output: {} as Outputs,
  },
  actions: {
    openDeleteConfirmation: openDeleteConfirmation,
    setDeaTableSort: assign({
      deaTable: setDeaTableSort,
    }),
    setCdsTableSort: assign({
      cdsTable: setCdsTableSort,
    }),
    closeAndClearModal: assign({
      modal: () => ({ edit: false, create: false, type: 'dea' as 'cds' | 'dea' }),
    }),
    setCdsCreateForm: assign({
      modal: () => ({ edit: false, create: true, type: 'cds' as 'cds' | 'dea' }),
      cdsLicenseForm: emptyCdsForm,
    }),
    setDeaCreateForm: assign({
      modal: () => ({ edit: false, create: true, type: 'dea' as 'cds' | 'dea' }),
      deaLicenseForm: emptyDeaForm,
    }),
    setCdsEditForm: assign({
      modal: () => ({ edit: true, create: false, type: 'cds' as 'cds' | 'dea' }),
      editId: ({ event }) => (event as OnEditEvent).id,
      cdsLicenseForm: populateCdsForm,
    }),
    setDeaEditForm: assign({
      modal: () => ({ edit: true, create: false, type: 'dea' as 'cds' | 'dea' }),
      editId: ({ event }) => (event as OnEditEvent).id,
      deaLicenseForm: populateDeaForm,
    }),
    setDeaFilter: assign({
      deaTable: setDeaTableFilter,
    }),
    setCdsFilter: assign({
      cdsTable: setCdsTableFilter,
    }),
    setCdsFromOutput: assign(setCdsFromOutput),
    setDeaFromOutput: assign(setDeaFromOutput),
    sendError: showAxiosErrorAction,
  },
}).createMachine({
  context: ({ input }) => ({
    activeTab: 0,
    providerId: input.providerId || -1,
    editId: -1,
    modal: {
      edit: false,
      create: false,
      type: 'dea',
    },
    loading: false,
    deaLicenses: [],
    cdsLicenses: [],
    deaLicenseForm: emptyDeaForm,
    cdsLicenseForm: emptyCdsForm,
    cdsTable: {
      pagination: {
        page: 1,
        pageSize: 100,
        total: 0,
      },
      filter: {
        active: true,
        inactive: true,
      },
      sort: {
        active: 'id',
        id: 1,
        issuingStateCode: 1,
        issuingAgency: 1,
        licenseTitle: 1,
        expiryDate: 1,
        issueDate: 1,
        licenseNumber: 1,
      },
    },
    deaTable: {
      pagination: {
        page: 1,
        pageSize: 100,
        total: 0,
      },
      filter: {
        active: true,
        inactive: true,
      },
      sort: {
        active: 'id',
        id: 1,
        deaNum: 1,
        issueDate: 1,
        expiryDate: 1,
        restrictedUseFlag: 1,
      },
    },
  }),
  on: {
    ON_SET_PROVIDER_ID: {
      actions: [
        assign({ providerId: ({ event }) => (event as OnSetProviderId).payload.providerId }),
      ],
    },
    ON_CANCEL_EDIT_OR_CREATE: {
      actions: [
        assign({
          modal: ({ context }) => ({
            ...context.modal,
            edit: false,
            create: false,
          }),
        }),
      ],
    },
    ON_SET_ACTIVE_TAB: [
      {
        target: '.$_GET_DEA_LICENSES',
        actions: [
          assign({
            activeTab: ({ event }) => (event as OnSetActiveTabEvent).index,
          }),
          assign({ loading: true }),
        ],
        guard: ({ event }): boolean =>
          LICENSES_TABS[(event as OnSetActiveTabEvent).index].type === 'dea',
      },
      {
        target: '.$_GET_CDS_LICENSES',
        actions: [
          assign({
            activeTab: ({ event }) => (event as OnSetActiveTabEvent).index,
          }),
          assign({ loading: true }),
        ],
        guard: ({ event }): boolean =>
          LICENSES_TABS[(event as OnSetActiveTabEvent).index].type === 'cds',
      },
    ],
  },
  initial: '$_DEA',
  states: {
    $_DEA: {
      on: {
        ON_LOAD: {
          target: '$_GET_DEA_LICENSES',
        },
        ON_CANCEL_EDIT_OR_CREATE: {
          actions: { type: 'closeAndClearModal' },
        },
        ON_ADD_LICENSE: {
          actions: { type: 'setDeaCreateForm' },
        },
        ON_EDIT_LICENSE: {
          actions: { type: 'setDeaEditForm' },
        },
        ON_FILTER_TABLE: {
          target: '$_GET_DEA_LICENSES',
          actions: { type: 'setDeaFilter' },
        },
        ON_SORT_TABLE: {
          target: '$_GET_DEA_LICENSES',
          actions: { type: 'setDeaTableSort' },
        },
        ON_DELETE: {
          target: '$_DELETE_DEA_LICENSE',
          actions: [
            ({ event }) =>
              console.log('[licenseForm.machine] $_DEA: ON_DELETE received', {
                id: (event as Extract<Events, { type: 'ON_DELETE' }>).payload?.id,
              }),
          ],
        },
        ON_SEND_DELETE_CONFIRMATION: {
          actions: [
            'openDeleteConfirmation',
            ({ context, event }) =>
              console.log('[licenseForm.machine] ON_SEND_DELETE_CONFIRMATION received', {
                id: (event as Extract<Events, { type: 'ON_SEND_DELETE_CONFIRMATION' }>).payload?.id,
              }),
          ],
        },
        ON_SUBMIT_LICENSE_UPDATE: {
          target: '$_UPDATE_DEA_LICENSE',
        },
        ON_SUBMIT_LICENSE_CREATE: {
          target: '$_CREATE_DEA_LICENSE',
        },
      },
    },
    $_CDS: {
      on: {
        ON_LOAD: {
          target: '$_GET_CDS_LICENSES',
        },
        ON_CANCEL_EDIT_OR_CREATE: {
          actions: { type: 'closeAndClearModal' },
        },
        ON_ADD_LICENSE: {
          actions: { type: 'setCdsCreateForm' },
        },
        ON_EDIT_LICENSE: {
          actions: { type: 'setCdsEditForm' },
        },
        ON_FILTER_TABLE: {
          target: '$_GET_CDS_LICENSES',
          actions: { type: 'setCdsFilter' },
        },
        ON_SORT_TABLE: {
          target: '$_GET_CDS_LICENSES',
          actions: {
            type: 'setCdsTableSort',
          },
        },
        ON_DELETE: {
          target: '$_DELETE_CDS_LICENSE',
        },
        ON_SEND_DELETE_CONFIRMATION: {
          actions: [
            'openDeleteConfirmation',
            ({ context, event }) =>
              console.log('[licenseForm.machine] ON_SEND_DELETE_CONFIRMATION received', {
                id: (event as Extract<Events, { type: 'ON_SEND_DELETE_CONFIRMATION' }>).payload?.id,
              }),
          ],
        },
        ON_SUBMIT_LICENSE_UPDATE: {
          target: '$_UPDATE_CDS_LICENSE',
        },
        ON_SUBMIT_LICENSE_CREATE: {
          target: '$_CREATE_CDS_LICENSE',
        },
      },
    },
    $_UPDATE_CDS_LICENSE: {
      invoke: {
        src: 'updateCdsLicense',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_GET_CDS_LICENSES',
          actions: 'closeAndClearModal',
        },
        onError: { target: '$_LOADING_ERROR', actions: ['sendError'] },
      },
    },
    $_CREATE_CDS_LICENSE: {
      invoke: {
        src: 'createCdsLicense',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_GET_CDS_LICENSES',
          actions: 'closeAndClearModal',
        },
        onError: { target: '$_LOADING_ERROR', actions: ['sendError'] },
      },
    },
    $_DELETE_CDS_LICENSE: {
      invoke: {
        src: 'deleteCdsLicense',
        input: ({ context, event }) => {
          console.log('[licenseForm.machine] $_DELETE_CDS_LICENSE: invoking delete API', {
            id: (event as Extract<Events, { type: 'ON_SEND_DELETE_CONFIRMATION' }>).payload?.id,
          });
          return { context, event };
        },
        onDone: {
          target: '$_GET_CDS_LICENSES',
          actions: [
            ({ event }) =>
              console.log('[licenseForm.machine] $_DELETE_CDS_LICENSE: delete successful', {
                output: (event as { output: unknown }).output,
              }),
          ],
        },
        onError: {
          target: '$_LOADING_ERROR',
          actions: [
            'sendError',
            ({ self }) =>
              console.log('[licenseForm.machine] $_DELETE_CDS_LICENSE: delete failed', { self }),
          ],
        },
      },
    },
    $_GET_CDS_LICENSES: {
      invoke: {
        src: 'getCdsLicenses',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_CDS',
          actions: ['setCdsFromOutput', assign({ loading: false })],
        },
        onError: { target: '$_LOADING_ERROR' },
      },
    },
    $_GET_DEA_LICENSES: {
      invoke: {
        src: 'getDeaLicenses',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_DEA',
          actions: ['setDeaFromOutput', assign({ loading: false })],
        },
        onError: { target: '$_LOADING_ERROR', actions: ['sendError'] },
      },
    },
    $_DELETE_DEA_LICENSE: {
      invoke: {
        src: 'deleteDeaLicense',
        input: ({ context, event }: Scope) => {
          console.log('[licenseForm.machine] $_DELETE_DEA_LICENSE: invoking delete API', {
            id: (event as Extract<Events, { type: 'ON_SEND_DELETE_CONFIRMATION' }>).payload?.id,
          });
          return { context, event };
        },
        onDone: {
          target: '$_GET_DEA_LICENSES',
          actions: [
            ({ event }) =>
              console.log('[licenseForm.machine] $_DELETE_DEA_LICENSE: delete successful', {
                output: (event as { output: unknown }).output,
              }),
          ],
        },
        onError: {
          target: '$_LOADING_ERROR',
          actions: [
            'sendError',
            ({ self }) =>
              console.log('[licenseForm.machine] $_DELETE_DEA_LICENSE: delete failed', { self }),
          ],
        },
      },
    },
    $_UPDATE_DEA_LICENSE: {
      invoke: {
        src: 'updateDeaLicense',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: { target: '$_GET_DEA_LICENSES', actions: 'closeAndClearModal' },
        onError: { target: '$_LOADING_ERROR', actions: ['sendError'] },
      },
    },
    $_CREATE_DEA_LICENSE: {
      invoke: {
        src: 'createDeaLicense',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_GET_DEA_LICENSES',
          actions: 'closeAndClearModal',
        },
        onError: { target: '$_LOADING_ERROR', actions: ['sendError'] },
      },
    },
    $_LOADING_ERROR: {
      always: [
        {
          target: '$_CDS',
          guard: ({ context }) => LICENSES_TABS[context.activeTab].type === 'cds',
          actions: ['sendError'],
        },
        {
          target: '$_DEA',
          actions: ['sendError'],
        },
      ],
    },
  },
});
